import { listsHaveMatchingElement } from "./condition";

export interface WithdrawalExclusion {
  collections?: string[] | null;
  tags?: string[] | null;
  productVariantMetafields?: string[] | null;
}

export interface WithdrawalExclusionItem {
  collections: string[];
  productTags: string[];
  /** Variant metafields keyed `namespace.key`. */
  productVariantMetafields: Record<string, string>;
}

/**
 * Whether an item matches any configured withdrawal exclusion — a collection
 * name, a product tag, or a variant metafield (`namespace.key` /
 * `namespace.key:value`). Matching mirrors the equivalent return-flow
 * conditions (collection/product-tag/variant-metafield).
 */
export function isExcludedFromWithdrawal(
  exclusion: WithdrawalExclusion | null | undefined,
  item: WithdrawalExclusionItem,
): boolean {
  if (!exclusion) {
    return false;
  }
  if (
    exclusion.collections?.length &&
    listsHaveMatchingElement(item.collections, exclusion.collections)
  ) {
    return true;
  }
  if (
    exclusion.tags?.length &&
    listsHaveMatchingElement(item.productTags, exclusion.tags)
  ) {
    return true;
  }
  for (const entry of exclusion.productVariantMetafields ?? []) {
    const metafield = parseMetafieldExclusion(entry);
    if (metafield && matchesVariantMetafield(metafield, item)) {
      return true;
    }
  }
  return false;
}

interface ParsedMetafieldExclusion {
  namespace: string;
  key: string;
  /** "exact" matches the full value; "contains" matches any present value. */
  comparison: "exact" | "contains";
  value: string;
}

/**
 * Parses a `namespace.key` or `namespace.key:value` exclusion string. Without a
 * value, matches any present value (CONTAINS ""); with a value, matches it
 * exactly.
 */
function parseMetafieldExclusion(
  entry: string,
): ParsedMetafieldExclusion | null {
  const colonIndex = entry.indexOf(":");
  const metafieldPart = colonIndex >= 0 ? entry.slice(0, colonIndex) : entry;
  const value = colonIndex >= 0 ? entry.slice(colonIndex + 1) : "";
  const dotIndex = metafieldPart.indexOf(".");
  if (dotIndex <= 0) {
    return null;
  }
  const namespace = metafieldPart.slice(0, dotIndex);
  const key = metafieldPart.slice(dotIndex + 1);
  if (!namespace || !key) {
    return null;
  }
  return { namespace, key, comparison: value ? "exact" : "contains", value };
}

function matchesVariantMetafield(
  metafield: ParsedMetafieldExclusion,
  item: WithdrawalExclusionItem,
): boolean {
  const value =
    item.productVariantMetafields[`${metafield.namespace}.${metafield.key}`];
  if (!value) {
    return false;
  }
  const itemValue = value.trim().toUpperCase();
  const exclusionValue = metafield.value.trim().toUpperCase();
  return metafield.comparison === "exact"
    ? itemValue === exclusionValue
    : itemValue.includes(exclusionValue);
}
